import { useCallback } from "react";

import type { AdminProfile, CosignatureSupervisorsResponse } from "@/core/types";
import { Select2, SelectSkeleton } from "@/ui2/molecules/Select";
import { useAdminProfile } from "@/portals/admin/(profile)/api/queries/useAdminProfile";
import { useCosignatureSupervisors } from "@/features/cosignature/api/queries/useCosignatureSupervisors";

import { ALL_OPTION_ID } from "../../constants";
import { SupervisorAssignmentOptionValue } from "../types";

type SupervisorAssignmentSelectProps = {
  onChange: (value: SupervisorAssignmentOptionValue | null) => void;
  value: SupervisorAssignmentOptionValue | null;
};

export function SupervisorAssignmentSelect({ onChange, value }: SupervisorAssignmentSelectProps) {
  const { data: username } = useAdminProfile({
    select: (profile) => profile.username,
  });

  const { data: options, isLoading } = useCosignatureSupervisors({
    select: (supervisors) => selectOptions(supervisors, username),
  });

  const handleChange = useCallback(
    (key: SupervisorAssignmentOptionValue) => {
      onChange(key === ALL_OPTION_ID ? null : key);
    },
    [onChange],
  );

  if (isLoading) return <SelectSkeleton width={240} />;

  return (
    <Select2
      className="tw:max-w-60 tw:shrink-0"
      label="Supervisor Assignment"
      onChange={handleChange}
      options={options}
      value={value ?? ALL_OPTION_ID}
    />
  );
}

function selectOptions(
  supervisors: CosignatureSupervisorsResponse,
  username: AdminProfile["username"],
) {
  return [
    { id: ALL_OPTION_ID, name: "All" },
    ...supervisors.map((supervisor) =>
      supervisor.id === username ? { ...supervisor, name: `Me (${supervisor.name})` } : supervisor,
    ),
  ];
}
